import { type Ref, useRef } from 'react'
import { Drawer as BaseDrawer } from '@base-ui/react/drawer'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import { confirmDialogOnEnter } from '@gto/helpers/dialog-keys'
import { cn } from '@gto/ui/cn'

import { SwipeArea } from './swipe-area'

const popupVariants = tv({
  base: cn(
    'fixed flex flex-col',
    'bg-paper-elevation3 shadow-drawer outline-none',
    'transition-transform duration-300 ease-out',
    // Track the user's finger during an active swipe gesture. Base UI sets
    // these CSS vars on the popup element while `data-swiping` is present;
    // without `transition-none` the popup would lag behind the pointer.
    'data-[swiping]:translate-x-[var(--drawer-swipe-movement-x,0px)] data-[swiping]:translate-y-[var(--drawer-swipe-movement-y,0px)] data-[swiping]:transition-none',
  ),
  variants: {
    side: {
      // `data-[closed]` keeps the popup off-screen while it stays mounted
      // (keepMounted) — without it, the closed-but-mounted popup would
      // sit at its default in-bounds position because Base UI only emits
      // `data-starting-style` / `data-ending-style` during animations.
      right: cn(
        'inset-y-0 right-0 h-full',
        'data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full data-[closed]:translate-x-full',
      ),
      left: cn(
        'inset-y-0 left-0 h-full',
        'data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full data-[closed]:-translate-x-full',
      ),
      bottom: cn(
        'inset-x-0 bottom-0 w-full',
        'data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full data-[closed]:translate-y-full',
      ),
      top: cn(
        'inset-x-0 top-0 w-full',
        'data-[starting-style]:-translate-y-full data-[ending-style]:-translate-y-full data-[closed]:-translate-y-full',
      ),
    },
    size: {
      sm: '',
      md: '',
      lg: '',
      full: '',
      auto: '',
    },
    /**
     * Stacking band. Defaults to `drawer` (1200). Use `modal` (1300) only for a
     * sheet opened from inside a Dialog — at the drawer band it would render
     * behind that Dialog — and `popover` (1350) for one opened from inside a
     * modal-band drawer or dialog. A variant rather than a `z-*` className because `cn`
     * is a bare twMerge and does not treat the non-numeric `z-drawer`/`z-modal`
     * as one class group, so an override class would not replace the base.
     */
    layer: {
      drawer: 'z-drawer',
      modal: 'z-modal',
      popover: 'z-popover',
    },
  },
  // Breakpoints in this preset are max-width: unprefixed = desktop default,
  // `sm:` = ≤640px. Side drawers are compact on desktop, full-bleed on mobile.
  compoundVariants: [
    { side: 'right', size: 'sm', className: 'w-[360px] sm:w-full' },
    { side: 'right', size: 'md', className: 'w-[444px] sm:w-full' },
    { side: 'right', size: 'lg', className: 'w-[540px] sm:w-full' },
    { side: 'right', size: 'full', className: 'w-full' },
    { side: 'left', size: 'sm', className: 'w-[360px] sm:w-full' },
    { side: 'left', size: 'md', className: 'w-[444px] sm:w-full' },
    { side: 'left', size: 'lg', className: 'w-[540px] sm:w-full' },
    { side: 'left', size: 'full', className: 'w-full' },
    { side: 'bottom', size: 'auto', className: 'h-auto max-h-[100dvh] rounded-t-md' },
    { side: 'bottom', size: 'sm', className: 'h-[40dvh] rounded-t-md' },
    { side: 'bottom', size: 'md', className: 'h-[60dvh] rounded-t-md' },
    { side: 'bottom', size: 'lg', className: 'h-[90dvh] rounded-t-md' },
    { side: 'bottom', size: 'full', className: 'h-dvh rounded-t-md' },
    { side: 'top', size: 'sm', className: 'h-[40dvh] rounded-b-md' },
    { side: 'top', size: 'auto', className: 'h-auto max-h-[100dvh] rounded-b-md' },
    { side: 'top', size: 'md', className: 'h-[60dvh] rounded-b-md' },
    { side: 'top', size: 'lg', className: 'h-[90dvh] rounded-b-md' },
    { side: 'top', size: 'full', className: 'h-dvh rounded-b-md' },
  ],
  defaultVariants: {
    side: 'right',
    size: 'md',
    layer: 'drawer',
  },
})

type PopupVariants = VariantProps<typeof popupVariants>

/** Base UI wraps the React event, so take the handler shape from its own props. */
type PopupKeyDownHandler = NonNullable<BaseDrawer.Popup.Props['onKeyDown']>

type PopupProps = BaseDrawer.Popup.Props &
  PopupVariants & {
    ref?: Ref<HTMLDivElement>
    /**
     * Whether Enter presses the call to action of this sheet. A sheet is the
     * phone form of a modal, so it confirms the same way. Turn it off when the
     * content owns Enter; when a confirmation covers the call to action and the
     * covered markup cannot be made `inert`; or when the action cannot be
     * undone — a delete with no undo should answer to a tap or a deliberate
     * Tab, never to a reflex press.
     * @default true
     */
    confirmOnEnter?: boolean
    /**
     * Suppress the auto-rendered grab handle. The handle shows by default on
     * `bottom`/`top` drawers below `full` size; pass this to hide it.
     */
    hideSwipeArea?: boolean
  }

// Base UI's documented structure is `Portal > Backdrop + Viewport > Popup`,
// so the Viewport is rendered here (rather than asking every consumer to
// add it manually) — keeps our component tree identical to the previous
// Dialog-based DS while still giving the Drawer primitive the wrapper it
// needs for swipe-gesture handling.
export const Popup = ({
  className,
  side,
  size,
  layer,
  confirmOnEnter = true,
  initialFocus,
  hideSwipeArea = false,
  children,
  ref,
  onKeyDown,
  onPointerDown,
  ...props
}: PopupProps) => {
  const popupRef = useRef<HTMLDivElement>(null)

  const handleKeyDown: PopupKeyDownHandler = (event) => {
    onKeyDown?.(event)
    if (!confirmOnEnter) {
      return
    }
    // A sheet renders in its own portal, so the helper answers only for the
    // sheet the key press came from; anything underneath stays quiet.
    confirmDialogOnEnter(event.nativeEvent, event.currentTarget)
  }

  // Top/bottom sheets read as dismissible only with a grab handle; full-height
  // sheets are takeovers, not sheets, so they omit it. Side drawers never use one.
  const showSwipeArea = !hideSwipeArea && (side === 'bottom' || side === 'top') && size !== 'full'

  return (
    <BaseDrawer.Viewport>
      <BaseDrawer.Popup
        {...props}
        ref={(node) => {
          popupRef.current = node
          if (typeof ref === 'function') {
            ref(node)
          } else if (ref) {
            ref.current = node
          }
        }}
        className={cn(popupVariants({ side, size, layer }), className)}
        initialFocus={initialFocus ?? popupRef}
        onKeyDown={handleKeyDown}
        onPointerDown={(event) => {
          // A mouse press inside the popup must not reach the Viewport's swipe
          // tracking (it lives on the popup's parent, so stopPropagation, not
          // preventBaseUIHandler): swipe-start takes pointer capture on the
          // popup, which retargets the ensuing click away from any
          // non-native-interactive target (plain divs, role="switch" spans),
          // silently swallowing the click. Touch/pen swiping (mobile
          // dismissal) is unaffected, and the grab handle stays draggable
          // with a mouse.
          if (
            event.pointerType === 'mouse' &&
            event.target instanceof Element &&
            event.target.closest('[data-tst="drawer-swipe-area"]') === null
          ) {
            event.stopPropagation()
          }
          onPointerDown?.(event)
        }}
      >
        {showSwipeArea && <SwipeArea />}
        {children}
      </BaseDrawer.Popup>
    </BaseDrawer.Viewport>
  )
}
